import { action, observable, makeObservable } from 'mobx';
import { Services } from '../services';
import { ImageCompression } from './image.compression';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg'];
export const MAX_UPLOAD_FILE_SIZE_MB = 10;
export const MAX_UPLOAD_FILE_SIZE_BYTE = 1024 * 1024 * MAX_UPLOAD_FILE_SIZE_MB;

export type ImageValidationError = 'FILE_TOO_LARGE' | 'FILE_NOT_SUPPORTED';

export interface UploadValidationRules {
	/** An empty list accepts any type. */
	allowedTypes: string[];
	/** Whether a file the browser reports no mime type for is accepted. */
	allowEmptyType: boolean;
	/** Prefix for the analytics event, e.g. `image` or `file`. */
	analyticsPrefix: string;
	/** Written out rather than derived from `allowedTypes`, because mime subtypes make poor labels. */
	displayTypes?: string[];
}

export const IMAGE_VALIDATION_RULES: UploadValidationRules = {
	allowedTypes: ALLOWED_IMAGE_TYPES,
	allowEmptyType: false,
	analyticsPrefix: 'image',
	displayTypes: ['JPG'],
};

export const FILE_TOOL_VALIDATION_RULES: UploadValidationRules = {
	allowedTypes: [],
	allowEmptyType: true,
	analyticsPrefix: 'file',
};

export function validateUploadFile(file: File, rules: UploadValidationRules): ImageValidationError | null {
	if (file.size > MAX_UPLOAD_FILE_SIZE_BYTE) {
		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.Upload,
			`${rules.analyticsPrefix}:large`,
			'large',
			file.size
		);
		return 'FILE_TOO_LARGE';
	}

	if (rules.allowedTypes.length === 0) {
		return null;
	}

	const fileType = file.type.toLowerCase();
	if (fileType === '' && rules.allowEmptyType) {
		return null;
	}

	if (!rules.allowedTypes.includes(fileType)) {
		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.Upload,
			`${rules.analyticsPrefix}:type`,
			fileType || 'unknown',
			1
		);
		return 'FILE_NOT_SUPPORTED';
	}

	return null;
}

export class UniversalFileUploadReader<T> {
	type: string;
	name: string;
	size: number;
	field: T;
	fileReader: FileReader | null;

	@observable buffer: Uint8Array<ArrayBuffer> | null = null;
	@observable isFailed: boolean = false;
	@observable isLoading: boolean = true;

	constructor(
		public file: File,
		field: T
	) {
		makeObservable(this);

		this.name = file.name;
		this.type = file.type;
		this.size = file.size;
		this.field = field;

		this.fileReader = new FileReader();
		this.fileReader.onerror = this.onFileError;
		this.fileReader.onload = this.onFileLoad;
		this.fileReader.readAsArrayBuffer(file);
	}

	@action.bound onFileError() {
		this.fileReader?.abort();
		this.isFailed = true;
		this.isLoading = false;
	}

	@action.bound onFileLoad() {
		this.isFailed = false;
		this.isLoading = false;
		if (this.fileReader?.result && this.fileReader.result instanceof ArrayBuffer) {
			this.buffer = new Uint8Array(this.fileReader.result);
		}
		this.fileReader = null;
	}
}

export function readFileBuffer(file: File): Promise<Uint8Array<ArrayBuffer>> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onload = () => {
			if (reader.result instanceof ArrayBuffer) {
				resolve(new Uint8Array(reader.result));
				return;
			}
			reject(new Error(`Unreadable file: ${file.name}`));
		};
		reader.onerror = () => {
			reader.abort();
			reject(new Error(`Unreadable file: ${file.name}`));
		};

		reader.readAsArrayBuffer(file);
	});
}

/** Falls back to the original bytes when compression fails. */
export async function readImageForUpload(file: File): Promise<Uint8Array<ArrayBuffer>> {
	try {
		return await ImageCompression.compressImage(file, { quality: 0.95 });
	} catch (error) {
		Services.Log.Logger.warn({ error }, 'Image compression failed, uploading original');
		return readFileBuffer(file);
	}
}
